import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { CardTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import {
  FLOATING_POPOVER_Z_INDEX,
  computePointAnchoredContextMenu,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { EM_DASH, cn } from "@/lib/utils";

import {
  FIRMWARE_RELEASE_NOTES_LIST_KEY,
  FIRMWARE_RELEASE_NOTE_PRODUCT_LINES,
  FIRMWARE_RELEASE_NOTE_SERIAL_DEVICE_COUNTS_KEY,
  fetchFirmwareReleaseNotes,
  fetchFirmwareReleaseNoteSerialDeviceCounts,
  firmwareReleaseNoteProductLineLabel,
  firmwareReleaseNoteUploadedByLabel,
  FIRMWARE_SERIAL_DEVICE_COUNT_HOVER,
  type FirmwareReleaseNoteProductLine,
  type FirmwareReleaseNoteRecord,
} from "./firmwareApi";
import { FirmwareDeferredDeviceCount } from "./FirmwareDeferredDeviceCount";
import { FirmwareReleaseNoteCommentsPanel } from "./FirmwareReleaseNoteCommentsPanel";
import { FirmwareReleaseNoteMarkdown } from "./FirmwareReleaseNoteMarkdown";
import {
  formatSelectedNoteIds,
  nextListSelection,
  parseSelectedNoteIds,
} from "./firmwareReleaseNoteSelection";

type NoteSortKey = "versionKey" | "productLine" | "releasedOn" | "firmwareCount" | "serialDeviceCount";

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

function firmwareFileNamesHint(row: FirmwareReleaseNoteRecord): string {
  const names = (row.firmwareFiles ?? []).map((file) => file.fileName ?? "").filter((name) => name.trim() !== "");
  if (names.length > 0) return names.join("\n");
  return `${row.firmwareCount} firmware file${row.firmwareCount === 1 ? "" : "s"}`;
}

export function FirmwareReleaseNotesSplit(props: {
  writesAllowed: boolean;
  onOpenReleasesForNote: (releaseNoteId: number) => void;
  onAddFirmwareFile: (releaseNoteId: number) => void;
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [productLine, setProductLine] = useState<FirmwareReleaseNoteProductLine | "all">("all");
  const [sortKey, setSortKey] = useState<NoteSortKey>("versionKey");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [selectedIds, setSelectedIds] = useState<number[]>(() => parseSelectedNoteIds(searchParams.get("ids")));
  const anchorIdRef = useRef<number | null>(selectedIds[0] ?? null);
  const [contextMenu, setContextMenu] = useState<{ noteId: number; x: number; y: number } | null>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const listQuery = useQuery({
    queryKey: [...FIRMWARE_RELEASE_NOTES_LIST_KEY, search, productLine],
    queryFn: () =>
      fetchFirmwareReleaseNotes({
        q: search.trim() || undefined,
        productLine,
        limit: 500,
      }),
  });

  const countsQuery = useQuery({
    queryKey: FIRMWARE_RELEASE_NOTE_SERIAL_DEVICE_COUNTS_KEY,
    queryFn: fetchFirmwareReleaseNoteSerialDeviceCounts,
    staleTime: 60_000,
  });
  const countsReady = countsQuery.isSuccess;
  const countByVersionKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of countsQuery.data?.counts ?? []) {
      map.set(row.versionKey, row.deviceCount);
    }
    return map;
  }, [countsQuery.data?.counts]);

  const bySkuByVersionKey = useMemo(() => {
    const map = new Map<string, FirmwareReleaseNoteRecord["serialDeviceCountBySku"]>();
    for (const row of countsQuery.data?.counts ?? []) {
      map.set(row.versionKey, row.deviceCountBySku ?? []);
    }
    return map;
  }, [countsQuery.data?.counts]);

  const rows = useMemo(() => {
    const list = (listQuery.data?.releaseNotes ?? []).map((row) => ({
      ...row,
      serialDeviceCount: countsReady ? (countByVersionKey.get(row.versionKey) ?? 0) : null,
      serialDeviceCountBySku: countsReady ? (bySkuByVersionKey.get(row.versionKey) ?? []) : [],
    }));
    list.sort((left, right) => {
      const sign = sortDir === "asc" ? 1 : -1;
      if (sortKey === "productLine") return sign * compareText(left.productLine, right.productLine);
      if (sortKey === "releasedOn") return sign * compareText(left.releasedOn ?? "", right.releasedOn ?? "");
      if (sortKey === "firmwareCount") return sign * (left.firmwareCount - right.firmwareCount);
      if (sortKey === "serialDeviceCount") {
        return sign * ((left.serialDeviceCount ?? 0) - (right.serialDeviceCount ?? 0));
      }
      return sign * compareText(left.versionKey, right.versionKey);
    });
    return list;
  }, [bySkuByVersionKey, countByVersionKey, countsReady, listQuery.data?.releaseNotes, sortDir, sortKey]);

  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedSet.has(row.id)),
    [rows, selectedSet],
  );

  useEffect(() => {
    const next = formatSelectedNoteIds(selectedIds);
    const current = searchParams.get("ids") ?? "";
    if (next === current) return;
    const params = new URLSearchParams(searchParams);
    if (next === "") params.delete("ids");
    else params.set("ids", next);
    setSearchParams(params, { replace: true });
  }, [searchParams, selectedIds, setSearchParams]);

  useLayoutEffect(() => {
    if (contextMenu == null) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computePointAnchoredContextMenu({
        clientX: contextMenu.x,
        clientY: contextMenu.y,
        menuWidth: 14 * 16,
        estimatedMenuHeight: 42,
      }),
    );
  }, [contextMenu]);

  useEffect(() => {
    if (contextMenu == null) return;
    const onPointerDown = (): void => {
      setContextMenu(null);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") setContextMenu(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  const onSort = (key: NoteSortKey) => {
    if (sortKey === key) setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "firmwareCount" || key === "versionKey" ? "desc" : "asc");
    }
  };

  const applySelection = (clickedId: number, modifiers: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => {
    const next = nextListSelection({
      visibleIds,
      selectedIds,
      clickedId,
      anchorId: anchorIdRef.current,
      modifiers,
    });
    anchorIdRef.current = next.anchorId;
    setSelectedIds(next.selectedIds);
  };

  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, rowId: number) => {
    event.preventDefault();
    applySelection(rowId, {
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    });
  };

  const onRowContextMenu = (event: MouseEvent<HTMLElement>, rowId: number) => {
    if (!props.writesAllowed) return;
    event.preventDefault();
    applySelection(rowId, { shiftKey: false, ctrlKey: false, metaKey: false });
    setContextMenu({ noteId: rowId, x: event.clientX, y: event.clientY });
  };

  const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, rowId: number) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    applySelection(rowId, {
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    });
  };

  const selectClassName = cn(
    "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm",
    "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="notes-search">Search release notes</Label>
            <Input
              id="notes-search"
              type="search"
              className="mt-1.5"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by version or changelog text"
            />
          </div>
          <div>
            <Label htmlFor="notes-line">Product line</Label>
            <select
              id="notes-line"
              className={cn(selectClassName, "mt-1.5")}
              value={productLine}
              onChange={(event) => setProductLine(event.target.value as FirmwareReleaseNoteProductLine | "all")}
            >
              <option value="all">All product lines</option>
              {FIRMWARE_RELEASE_NOTE_PRODUCT_LINES.map((line) => (
                <option key={line} value={line}>
                  {firmwareReleaseNoteProductLineLabel(line)}
                </option>
              ))}
            </select>
          </div>
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[max-content_minmax(0,1fr)] lg:items-stretch">
        <HubViewportCard className="w-full overflow-hidden lg:w-max">
          <CardHeader className="flex shrink-0 flex-row flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <CardTitleWithHelp hoverHint="Control or Command click adds rows. Shift click selects a range.">
              Release notes
            </CardTitleWithHelp>
            <div className="flex items-center gap-2">
              {rows.length > 0 ? (
                <p className="text-xs font-normal text-slate-500">
                  Showing {rows.length} of {listQuery.data?.total ?? rows.length}
                </p>
              ) : null}
              {listQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden /> : null}
            </div>
          </CardHeader>
          <CardBody className="flex min-h-0 flex-1 flex-col p-0">
            {listQuery.isLoading ? (
              <p className="flex items-center gap-2 px-5 py-4 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading release notes…
              </p>
            ) : listQuery.isError ? (
              <p className="px-5 py-4 text-sm text-red-700">{(listQuery.error as Error).message}</p>
            ) : rows.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-600">No release notes match these filters.</p>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
                <table className="w-max border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 text-slate-500">
                    <tr>
                      <SortableTableHeader
                        label="Version"
                        sortKey="versionKey"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        className="w-[1%] whitespace-nowrap"
                      />
                      <SortableTableHeader
                        label="Line"
                        sortKey="productLine"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        className="whitespace-nowrap"
                      />
                      <SortableTableHeader
                        label="Files"
                        sortKey="firmwareCount"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="right"
                        className="whitespace-nowrap"
                      />
                      <SortableTableHeader
                        label="Devices"
                        sortKey="serialDeviceCount"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="right"
                        className="whitespace-nowrap"
                      />
                      <SortableTableHeader
                        label="Released"
                        sortKey="releasedOn"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        className="whitespace-nowrap pr-3"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row: FirmwareReleaseNoteRecord) => {
                      const isSelected = selectedSet.has(row.id);
                      return (
                        <tr
                          key={row.id}
                          role="button"
                          tabIndex={0}
                          aria-selected={isSelected}
                          onClick={(event) => onRowClick(event, row.id)}
                          onContextMenu={(event) => onRowContextMenu(event, row.id)}
                          onKeyDown={(event) => onRowKeyDown(event, row.id)}
                          className={cn(
                            "cursor-pointer select-none border-b border-slate-100 hover:bg-slate-50",
                            isSelected && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                        >
                          <td className="whitespace-nowrap px-2 py-2 font-medium text-slate-900">{row.versionKey}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-slate-700">
                            {firmwareReleaseNoteProductLineLabel(row.productLine)}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                            {row.firmwareCount > 0 ? (
                              <HoverHintTrigger
                                closeOnTriggerActivate
                                trigger={
                                  <button
                                    type="button"
                                    className="text-sky-700 hover:underline"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      props.onOpenReleasesForNote(row.id);
                                    }}
                                  >
                                    {row.firmwareCount}
                                  </button>
                                }
                              >
                                <pre className="max-w-sm whitespace-pre-wrap font-sans text-xs text-slate-800">
                                  {firmwareFileNamesHint(row)}
                                </pre>
                              </HoverHintTrigger>
                            ) : (
                              <span>{row.firmwareCount}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                            <FirmwareDeferredDeviceCount
                              count={row.serialDeviceCount}
                              ready={countsReady}
                              hoverHint={FIRMWARE_SERIAL_DEVICE_COUNT_HOVER}
                              bySku={row.serialDeviceCountBySku ?? []}
                            />
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 pr-3 text-slate-700">{row.releasedOn ?? EM_DASH}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </HubViewportCard>

        <HubViewportCard className="min-w-0 overflow-hidden">
          <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>
              {selectedRows.length === 0
                ? "Notes"
                : selectedRows.length === 1
                  ? selectedRows[0].versionKey
                  : `${selectedRows.length} versions`}
            </CardTitle>
          </CardHeader>
          <CardBody className="min-h-0 flex-1 overflow-y-auto">
            {selectedRows.length === 0 ? (
              <p className="text-sm text-slate-600">
                Select one or more versions on the left to read the changelog here.
              </p>
            ) : (
              <div className="space-y-6">
                {selectedRows.map((row) => (
                  <section
                    key={row.id}
                    className="space-y-2"
                    onContextMenu={(event) => onRowContextMenu(event, row.id)}
                  >
                    {selectedRows.length > 1 ? (
                      <h4 className="text-sm font-semibold text-slate-900">{row.versionKey}</h4>
                    ) : null}
                    <p className="text-xs text-slate-500">
                      {firmwareReleaseNoteProductLineLabel(row.productLine)}
                      {row.releasedOn ? ` · Released ${row.releasedOn}` : ""}
                      {` · ${row.firmwareCount} firmware file${row.firmwareCount === 1 ? "" : "s"}`}
                      {countsReady
                        ? ` · ${row.serialDeviceCount ?? 0} device${row.serialDeviceCount === 1 ? "" : "s"} reporting this version`
                        : ""}
                      {` · Uploaded by ${firmwareReleaseNoteUploadedByLabel(row)}`}
                    </p>
                    <FirmwareReleaseNoteMarkdown text={row.notes} />
                    {row.comments ? (
                      <p className="whitespace-pre-wrap text-xs text-slate-500">Legacy internal note: {row.comments}</p>
                    ) : null}
                    <FirmwareReleaseNoteCommentsPanel releaseNoteId={row.id} writesAllowed={props.writesAllowed} />
                  </section>
                ))}
              </div>
            )}
          </CardBody>
        </HubViewportCard>
      </div>

      {contextMenu != null && menuBox != null
        ? createPortal(
            <div
              className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: menuBox.left,
                top: menuBox.top,
                width: menuBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  const noteId = contextMenu.noteId;
                  setContextMenu(null);
                  props.onAddFirmwareFile(noteId);
                }}
              >
                Add new firmware file
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
